import React from 'react';
import { ReactComponent as ChevronRightIcon } from '../../../shared/icons/chevron-right.svg';
import { ReactComponent as ChevronDownIcon } from '../../../shared/icons/chevron-down.svg';
import styles from '../DataPage.module.scss';
import {
  formatCurrency,
  formatCurrencyWithDecimals,
  formatDate,
  formatBoolean,
  formatNumber,
  formatText,
} from '../utils/dataFormatters';

interface CellProps {
  value: any;
  row?: any;
}

interface DocumentNameCellProps extends CellProps {
  expandedRowIds: Set<string>;
  setExpandedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Document Name Cell - Handles expandable rows with amendments
 */
export const DocumentNameCell: React.FC<DocumentNameCellProps> = ({
  value,
  row,
  expandedRowIds,
  setExpandedRowIds,
}) => {
  const isAmendment = row.original.isAmendment;
  const amendmentCount = row.original.subRows?.length || 0;
  const hasSubRows = amendmentCount > 0;
  const isExpanded = expandedRowIds.has(row.original.id);

  // Subrow rendering (amendments)
  if (isAmendment) {
    return (
      <div className={styles.subRowContent}>
        <span className={styles.subRowConnector}>└─</span>
        <div className={styles.documentInfo}>
          <div className={styles.documentTitle}>{value}</div>
        </div>
      </div>
    );
  }

  // Parent row rendering
  return (
    <div className={styles.documentNameCell}>
      {/* Expand icon - only show for rows with amendments */}
      {hasSubRows && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandedRowIds((prev) => {
              const newSet = new Set(prev);
              if (newSet.has(row.original.id)) {
                newSet.delete(row.original.id);
              } else {
                newSet.add(row.original.id);
              }
              return newSet;
            });
          }}
          className={styles.expandButton}
          aria-label={isExpanded ? 'Collapse amendments' : 'Expand amendments'}
        >
          {isExpanded ? (
            <ChevronDownIcon width={16} height={16} />
          ) : (
            <ChevronRightIcon width={16} height={16} />
          )}
        </button>
      )}

      {/* Document title and amendment count */}
      <div className={styles.documentInfo}>
        <div className={styles.documentTitle}>{value}</div>
        {amendmentCount > 0 && (
          <div className={styles.amendmentCount}>
            +{amendmentCount} amendment{amendmentCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Plain text cell renderer
 */
export const TextCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatText(value)}</div>
);

/**
 * Currency cell renderer (no decimals)
 */
export const CurrencyCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatCurrency(value)}</div>
);

/**
 * Currency cell renderer (with decimals)
 */
export const CurrencyDecimalCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatCurrencyWithDecimals(value)}</div>
);

/**
 * Date cell renderer
 */
export const DateCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatDate(value)}</div>
);

/**
 * Boolean cell renderer (Yes/No)
 */
export const BooleanCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatBoolean(value)}</div>
);

/**
 * Number cell renderer (plain numbers without currency)
 */
export const NumberCell: React.FC<CellProps> = ({ value }) => (
  <div>{formatNumber(value)}</div>
);
